import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { User } from '../../../../Types/User';
import * as colors from '../../../../Util/Colors';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import CustomLegend from '../../DashboardComponents/LighthouseMetrics/CustomLegend';
import CustomTooltip from '../../DashboardComponents/LighthouseMetrics/CustomTooltip';
import {
  domainCalculator,
  MetricBooleanMap,
  parseFixedString,
  PERCENTAGE_TABLE,
  TIME_TABLE,
} from './helper';
import { GeneralMetricType } from './types';
import LegendIcon from '../CustomLegendIcon';

interface SubmetricTrendProps {
  metrics: GeneralMetricType;
  user: User;
  type: string;
  isProfileTm: boolean;
}

// Variable to allow line animation to trigger when first loaded only
let runOnce = true;
const RENDER_DELAY = 10;

const DisplayTrend: React.FC<SubmetricTrendProps> = ({
  metrics,
  type,
  user,
  isProfileTm,
}: SubmetricTrendProps): React.ReactElement => {
  const intl = useIntl();

  const [displayChart, setDisplayChart] = useState(false);
  const legacyExperience = !isFeatureEnabled(Feature.LIGHTHOUSE);

  useEffect(() => {
    setDisplayChart(false);
    setTimeout(() => {
      setDisplayChart(true);
    }, RENDER_DELAY);
  }, [metrics]);

  const duplicateTrend = JSON.parse(
    JSON.stringify(metrics)
  ) as GeneralMetricType;

  const isPercentage = PERCENTAGE_TABLE[type as keyof MetricBooleanMap];
  const isTime = TIME_TABLE[type as keyof MetricBooleanMap];

  // eslint-disable-next-line array-callback-return
  duplicateTrend?.trend?.map(dp => {
    if (dp.datapoint) {
      dp.datapoint =
        type === 'ACHT' || type === 'HOLD_TIME' || type === 'ACW'
          ? dp.datapoint.replace(':', '.')
          : parseFixedString(parseFloat(dp.datapoint), isPercentage, true);
    }
    if (dp.networkAverage) {
      dp.networkAverage =
        type === 'ACHT' || type === 'HOLD_TIME' || type === 'ACW'
          ? dp.networkAverage.replace(':', '.')
          : parseFixedString(parseFloat(dp.networkAverage), isPercentage, true);
    }
  });

  const [dataMin, dataMax] = domainCalculator(
    duplicateTrend.trend,
    isPercentage,
    isTime
  );

  const checkAllNull = (): boolean => {
    let isAllNull: boolean = true;
    duplicateTrend!.trend!.forEach(dp => {
      if (dp.datapoint !== null || dp.networkAverage !== null) {
        isAllNull = false;
      }
    });
    return isAllNull;
  };
  const isDataExist = !checkAllNull();

  return (
    <div
      style={{
        display: displayChart ? 'block' : 'none',
      }}
    >
      <Row spacingInset='500 none 400 none'>
        <Text type='b200'>
          <b>
            <FormattedMessage id='cs_perfportal_trend' defaultMessage='Trend' />
          </b>
        </Text>
      </Row>
      <Row
        width='100%'
        widths='fill'
        spacing='none'
        spacingInset='none none 500 none'
      >
        <Column height='384px' heights='fit' alignmentHorizontal='center'>
          <ResponsiveContainer width='99%' height={384}>
            <LineChart
              data={duplicateTrend.trend!}
              margin={{ top: 5, right: 30, left: -25, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='timestamp'
                interval={0}
                dx={15}
                dy={20}
                angle={45}
                tick={{ fontSize: 10 }}
                padding={{ left: 20, right: 20 }}
                minTickGap={-200}
              >
                {!isDataExist && (
                  <Label
                    value={intl.formatMessage({
                      id: 'cs_perfportal_noDataToReport',
                      defaultMessage: 'No data to report',
                    })}
                    position='top'
                  />
                )}
              </XAxis>
              <YAxis
                type='number'
                allowDecimals={false}
                tick={{ fontSize: 10 }}
                domain={[dataMin, dataMax]}
                allowDataOverflow
                padding={{ top: 20, bottom: 20 }}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    isPercentage={
                      PERCENTAGE_TABLE[type as keyof MetricBooleanMap]
                    }
                    type={type}
                    user={user}
                    isProfileTm={isProfileTm}
                    isTime={TIME_TABLE[type as keyof MetricBooleanMap]}
                  />
                }
              />
              <Line
                dataKey='datapoint'
                type='monotone'
                name={type}
                stroke={colors.normalFill}
                strokeWidth={3}
                dot={{
                  r: 8,
                  stroke: colors.primaryStroke,
                  strokeWidth: 2,
                  fill: colors.primaryFill,
                }}
                isAnimationActive={runOnce}
              />
              <Legend
                verticalAlign='bottom'
                align='left'
                iconSize={12}
                wrapperStyle={{
                  fontSize: '14px',
                  marginLeft: '50px',
                }}
                content={(props: any): React.ReactElement => (
                  <CustomLegend
                    payload={props.payload}
                    user={user}
                    isProfileTm={isProfileTm}
                    type={type}
                  />
                )}
              />
            </LineChart>
          </ResponsiveContainer>
        </Column>
      </Row>
    </div>
  );
};

export default DisplayTrend;
